import { format } from 'date-fns';
import DataGrid, { Column, Editing, LoadPanel, RemoteOperations, RequiredRule, Scrolling, StringLengthRule } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from "devextreme/data/data_source";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { deleteStepRevisionComment, getStepRevisionComments, updateStepRevisionComment } from '../../../../api/v2/StepsApi';
import { CommentDto } from '../../../../dtos/CommentDto';
import { FileDto } from "../../../../dtos/FileDto";
import { GrantDto } from '../../../../dtos/GrantDto';
import { StepDto } from "../../../../dtos/StepDto";
import { PermissionType } from '../../../../dtos/PermissionType';
import { StepStatus } from '../../../../dtos/StepStatus';
import { UserRole } from "../../../../dtos/UserRole";
import LinkifiedText from "../../LinkifiedText";
import { useAppContext } from "../../AppContext";
import { checkStepPermission, getUserFullName } from '../../Utils';
import { useTaskListContext } from "../context/TaskListContext";

interface ISubTaskRevisionCommentsProps {
    grant: GrantDto;
    step: StepDto;
    isReadOnly: boolean;
}

export default function SubTaskRevisionComments({ grant, step, isReadOnly }: ISubTaskRevisionCommentsProps) {
    const gridRef = useRef<DataGrid<CommentDto, string>>(null);
    const taskListContext = useTaskListContext();
    const app = useAppContext();

    const [showGrid, setShowGrid] = useState(false);

    const dataSource = useMemo(() => {
        return new DataSource<CommentDto, string>({
            paginate: false,
            requireTotalCount: false,
            store: new CustomStore<CommentDto, string>({
                key: 'id',
                load: async () => {
                    return step.revisionRequestComments;
                },
                update: async (id: string, comment: CommentDto) => {
                    var result = await updateStepRevisionComment(step.id, id, comment);
                    if (result?.error != null)
                        app.errorToast(result.error.message);
                },
                remove: async (key) => {
                    var result = await deleteStepRevisionComment(step.id, key);
                    if (result?.error != null)
                        app.errorToast(result.error.message);
                }
            })
        });
    }, [step.id, app]);

    useEffect(() => {
        dataSource.load().then((data) => {
            setShowGrid(data.length > 0);
        });
    }, [step.revisionRequestComments, dataSource]);

    const allowDeleting = (fileData: any): boolean => {

        if (step.status == StepStatus.NOT_STARTED ||
            step.status == StepStatus.INACTIVE ||
            step.status == StepStatus.APPROVED)
            return false;

        // Use config-based permission check for status-based access
        // Pass grant.id for resolving external user roles
        const selectedPhase = taskListContext.getSelectedPhase();
        const allowedByStatus = checkStepPermission(step, app, PermissionType.Comment, false, selectedPhase, grant?.id);

        var allowedByRole = false;

        var data: FileDto = fileData?.row?.data;

        if (data != null) {
            var uploaderRole = data.user?.role;

            if (uploaderRole == UserRole.PI) {
                allowedByRole = app.UserIsPI || app.UserIsSO || app.UserIsPO || app.UserIsGMS || app.UserIsSuper;
            } else if (uploaderRole == UserRole.SO) {
                allowedByRole = app.UserIsSO || app.UserIsPO || app.UserIsGMS || app.UserIsSuper;
            } else if (uploaderRole == UserRole.PO) {
                allowedByRole = app.UserIsPO || app.UserIsSuper;
            } else if (uploaderRole == UserRole.GMS) {
                allowedByRole = app.UserIsGMS || app.UserIsSuper;
            } else if (uploaderRole == UserRole.SU) {
                allowedByRole = app.UserIsSuper;
            }
        }

        return allowedByStatus && allowedByRole;
    }

    const isVisible = () => {

        if (step.status == StepStatus.NOT_STARTED ||
            step.status == StepStatus.INACTIVE) {
            return false;
        }

        // Use config-based permission check for visibility
        // Pass grant.id for resolving external user roles
        const selectedPhase = taskListContext.getSelectedPhase();
        return checkStepPermission(step, app, PermissionType.Comment, true, selectedPhase, grant?.id);
    }

    const canEdit = (e: any) => {

        if (isReadOnly)
            return false;

        // Use config-based permission check for editing
        // Pass grant.id for resolving external user roles
        const selectedPhase = taskListContext.getSelectedPhase();
        return checkStepPermission(step, app, PermissionType.Comment, true, selectedPhase, grant?.id);
    }

    const nameRender = (e: any) => {
        var user = e.data.createdByUser;
        return getUserFullName(user);
    }

    const getFormattedDate = (input: Date | string | null | undefined): string => {
        if (!input) return '';

        try {
            const date = input instanceof Date ? input : new Date(input);

            if (isNaN(date.getTime())) {
                console.error('Invalid date:', input);
                return '';
            }

            return format(date, 'MM/dd/yyyy h:mm a');
        } catch (err) {
            console.error('Error formatting date:', err);
            return '';
        }
    }

    const commentRender = (data: any) => <LinkifiedText text={data.data.comment} />;

    const dateRender = (data: any) => (
        <div>
            {
                data.data.dateUpdated ?
                    (
                        <div>{getFormattedDate(data.data.dateUpdated)}</div>
                    ) : <></>
            }
        </div>
    );

    const safeParseDate = (input?: string | null) => {
        if (!input) return null;

        // Trim to max 3 digits in fractional seconds
        const cleaned = input.replace(/\.(\d{3})\d*$/, '.$1');
        const date = new Date(cleaned);

        return isNaN(date.getTime()) ? null : date;
    };

    if (!showGrid)
        return null;
    else {
        return (
            <>
                <h6 style={{ color: "#E91E63" }}>Revision Requests</h6>
                <DataGrid
                    dataSource={dataSource}
                    height="auto"
                    repaintChangesOnly={true}
                    rowAlternationEnabled={true}
                    showColumnHeaders={false}
                    showBorders={false}
                    showColumnLines={false}
                    showRowLines={false}
                    wordWrapEnabled={true}
                    ref={gridRef}
                >
                    <LoadPanel enabled={false} />

                    <Scrolling mode="virtual" showScrollbar="always"></Scrolling>
                    <Editing
                        mode="row"
                        allowAdding={false}
                        allowDeleting={false}
                        allowUpdating={false}
                        confirmDelete={true}
                        useIcons={true}
                    >
                    </Editing>
                    {/*<Paging defaultPageSize={100} />*/}
                    <RemoteOperations
                        filtering={true}
                        paging={false}
                        sorting={true}
                        summary={true}
                        grouping={true}
                        groupPaging={true}
                    />
                    {/*<Scrolling mode="virtual"></Scrolling>*/}
                    <Column
                        allowEditing={false}
                        allowSorting={false}
                        cellRender={commentRender}
                        dataField="comment"
                        dataType="string"
                        editorOptions={{ maxLength: 2000 }}
                    >
                        <RequiredRule></RequiredRule>
                        <StringLengthRule max={2000} message="Max length for a comment is 2000 characters"></StringLengthRule>
                    </Column>
                    {/*    <Column*/}
                    {/*        allowEditing={false}*/}
                    {/*        allowSorting={false}*/}
                    {/*        caption="User"*/}
                    {/*        cellRender={nameRender}*/}
                    {/*        dataField="updatedByUser"*/}
                    {/*        dataType="string"*/}
                    {/*        editCellRender={nameRender}*/}
                    {/*        width="150px"*/}
                    {/*    ></Column>*/}
                    <Column
                        allowEditing={false}
                        allowSorting={false}
                        caption="Date"
                        cellRender={dateRender}
                        dataField="dateUpdated"
                        dataType="string"
                        sortOrder="desc"
                        width="150px"
                    ></Column>
                </DataGrid>
            </>
        );
    }
}